import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { enrichBookIds, GOOGLE_BOOKS_ATTRIBUTION } from '../utils/curationEnrichment';
import { Plus, Pencil, Trash2, X, Save, Search, BookOpen } from 'lucide-react';

// Personal, named "shop" curations -- a reader's own hand-picked selection
// of books (by Google Books volume ID), separate from their library status
// tabs and from the real-world MyShelves.js locations. Mirrors that
// component's create/edit/delete UX. Book metadata for display is never
// stored here -- only external_id references in user_curations.book_ids --
// it's resolved at render time via curationEnrichment.js (book_cache first,
// Google Books API as fallback), per Google Books ToS attribution rules.

const emptyDraft = { title: '', description: '' };

const MyCurations = () => {
  const { user } = useAuth();
  const [curations, setCurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // null = not editing, 'new' = creating
  const [draft, setDraft] = useState(emptyDraft);
  const [activeCurationId, setActiveCurationId] = useState(null);
  const [enrichedBooks, setEnrichedBooks] = useState([]);
  const [booksLoading, setBooksLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const fetchCurations = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('user_curations')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true });
    if (!error && data) {
      setCurations(data);
      setActiveCurationId(prev => prev && data.some(c => c.id === prev) ? prev : (data[0]?.id || null));
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchCurations(); }, [fetchCurations]);

  const activeCuration = curations.find(c => c.id === activeCurationId);

  useEffect(() => {
    let cancelled = false;
    const loadBooks = async () => {
      if (!activeCuration) {
        setEnrichedBooks([]);
        return;
      }
      setBooksLoading(true);
      const books = await enrichBookIds(activeCuration.book_ids);
      if (!cancelled) setEnrichedBooks(books);
      setBooksLoading(false);
    };
    loadBooks();
    return () => { cancelled = true; };
  }, [activeCuration]);

  const startCreate = () => {
    setDraft(emptyDraft);
    setEditingId('new');
  };

  const startEdit = (curation) => {
    setDraft({ title: curation.title, description: curation.description || '' });
    setEditingId(curation.id);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(emptyDraft);
  };

  const saveDraft = async () => {
    if (!user || !draft.title.trim()) return;
    if (editingId === 'new') {
      const { data, error } = await supabase
        .from('user_curations')
        .insert([{
          user_id: user.id,
          title: draft.title.trim(),
          description: draft.description.trim() || null,
          book_ids: [],
        }])
        .select();
      if (!error && data) {
        setCurations(prev => [...prev, data[0]]);
        setActiveCurationId(data[0].id);
      }
    } else {
      const { error } = await supabase
        .from('user_curations')
        .update({
          title: draft.title.trim(),
          description: draft.description.trim() || null,
        })
        .eq('id', editingId);
      if (!error) {
        setCurations(prev => prev.map(c => c.id === editingId
          ? { ...c, title: draft.title.trim(), description: draft.description.trim() || null }
          : c));
      }
    }
    cancelEdit();
  };

  const deleteCuration = async (curationId) => {
    if (!window.confirm('Delete this curation? This only removes the list, not your library books.')) return;
    const { error } = await supabase.from('user_curations').delete().eq('id', curationId);
    if (!error) {
      setCurations(prev => prev.filter(c => c.id !== curationId));
      if (activeCurationId === curationId) setActiveCurationId(null);
    }
  };

  const searchGoogleBooks = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const apiKey = process.env.REACT_APP_GOOGLE_BOOKS_API_KEY;
      const url = `https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(searchQuery)}&maxResults=10${apiKey ? `&key=${apiKey}` : ''}`;
      const response = await fetch(url);
      const data = await response.json();
      setSearchResults(data.items || []);
    } catch (e) {
      console.error('Book search failed', e);
    }
    setSearching(false);
  };

  const addBookToCuration = async (volumeId) => {
    if (!activeCuration) return;
    if ((activeCuration.book_ids || []).includes(volumeId)) return;
    const nextIds = [...(activeCuration.book_ids || []), volumeId];
    const { error } = await supabase
      .from('user_curations')
      .update({ book_ids: nextIds })
      .eq('id', activeCuration.id);
    if (!error) {
      setCurations(prev => prev.map(c => c.id === activeCuration.id ? { ...c, book_ids: nextIds } : c));
    }
  };

  const removeBookFromCuration = async (volumeId) => {
    if (!activeCuration) return;
    const nextIds = (activeCuration.book_ids || []).filter(id => id !== volumeId);
    const { error } = await supabase
      .from('user_curations')
      .update({ book_ids: nextIds })
      .eq('id', activeCuration.id);
    if (!error) {
      setCurations(prev => prev.map(c => c.id === activeCuration.id ? { ...c, book_ids: nextIds } : c));
    }
  };

  if (loading) return <div style={{ padding: '20px', color: 'var(--text-soft)' }}>Loading curations…</div>;

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '14px' }}>
        {curations.map(curation => (
          <button
            key={curation.id}
            onClick={() => setActiveCurationId(curation.id)}
            style={{
              padding: '6px 14px',
              borderRadius: '14px',
              border: '1px solid ' + (curation.id === activeCurationId ? 'var(--accent)' : 'var(--border)'),
              backgroundColor: 'var(--bg-card)',
              color: 'var(--text)',
              fontWeight: curation.id === activeCurationId ? 'bold' : 'normal',
              cursor: 'pointer',
              fontSize: '0.85rem'
            }}
          >
            {curation.title}
          </button>
        ))}
        <button
          onClick={startCreate}
          style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '6px 12px', borderRadius: '14px',
            border: '1px dashed var(--border)', backgroundColor: 'transparent',
            color: 'var(--text-soft)', cursor: 'pointer', fontSize: '0.85rem'
          }}
        >
          <Plus size={14} /> New Curation
        </button>
      </div>

      {editingId !== null && (
        <div style={{
          border: '1px solid var(--border)', borderRadius: '12px', padding: '14px',
          marginBottom: '16px', backgroundColor: 'var(--bg-card)'
        }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
            <input
              value={draft.title}
              onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
              placeholder="Curation name (e.g. Cozy Autumn Reads)"
              style={{ flex: '1 1 220px', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--border)' }}
            />
            <input
              value={draft.description}
              onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
              placeholder="Description (optional)"
              style={{ flex: '1 1 220px', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--border)' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={saveDraft} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '8px', border: 'none', backgroundColor: 'var(--accent)', color: '#fff', cursor: 'pointer' }}>
              <Save size={14} /> Save
            </button>
            <button onClick={cancelEdit} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--border)', backgroundColor: 'transparent', cursor: 'pointer' }}>
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      {curations.length === 0 && editingId === null && (
        <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text-soft)' }}>
          No curations yet. Create one to start building your own personal book selection.
        </div>
      )}

      {activeCuration && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <div>
              <h4 style={{ margin: 0 }}>{activeCuration.title}</h4>
              {activeCuration.description && (
                <div style={{ fontSize: '0.8rem', color: 'var(--text-soft)' }}>{activeCuration.description}</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={() => startEdit(activeCuration)} style={{ padding: '6px', borderRadius: '8px', border: '1px solid var(--border)', backgroundColor: 'transparent', cursor: 'pointer' }} title="Edit curation">
                <Pencil size={14} />
              </button>
              <button onClick={() => deleteCuration(activeCuration.id)} style={{ padding: '6px', borderRadius: '8px', border: '1px solid var(--border)', backgroundColor: 'transparent', cursor: 'pointer', color: '#c0392b' }} title="Delete curation">
                <Trash2 size={14} />
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && searchGoogleBooks()}
              placeholder="Search for a book to add…"
              style={{ flex: 1, padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--border)' }}
            />
            <button onClick={searchGoogleBooks} disabled={searching} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', backgroundColor: 'transparent', cursor: 'pointer' }}>
              <Search size={14} /> {searching ? 'Searching…' : 'Search'}
            </button>
          </div>

          {searchResults.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
              {searchResults.map(item => (
                <button
                  key={item.id}
                  onClick={() => addBookToCuration(item.id)}
                  title={`Add "${item.volumeInfo?.title}"`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px',
                    borderRadius: '10px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-card)',
                    cursor: 'pointer', fontSize: '0.8rem', maxWidth: '220px'
                  }}
                >
                  <Plus size={12} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.volumeInfo?.title}
                  </span>
                </button>
              ))}
            </div>
          )}

          <div style={{
            backgroundColor: 'var(--bg-card)',
            borderRadius: '10px',
            padding: '16px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '14px',
            minHeight: '90px',
          }}>
            {booksLoading && <div style={{ color: 'var(--text-soft)', fontSize: '0.85rem' }}>Loading books…</div>}
            {!booksLoading && enrichedBooks.length === 0 && (
              <div style={{ color: 'var(--text-soft)', opacity: 0.7, fontSize: '0.85rem', padding: '8px' }}>
                No books in this curation yet. Search above to add some.
              </div>
            )}
            {!booksLoading && enrichedBooks.map(book => (
              <div key={book.id} style={{ width: '100px', textAlign: 'center', position: 'relative' }}>
                <button
                  onClick={() => removeBookFromCuration(book.id)}
                  title="Remove from curation"
                  style={{
                    position: 'absolute', top: '-6px', right: '-6px', width: '20px', height: '20px',
                    borderRadius: '50%', border: 'none', backgroundColor: '#c0392b', color: '#fff',
                    cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                >
                  <X size={12} />
                </button>
                {book.cover ? (
                  <img src={book.cover} alt={book.title} style={{ width: '70px', height: '100px', objectFit: 'cover', borderRadius: '4px', boxShadow: '1px 1px 4px rgba(0,0,0,0.3)' }} />
                ) : (
                  <div style={{ width: '70px', height: '100px', borderRadius: '4px', backgroundColor: 'var(--bg-nav)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                    <BookOpen size={20} color="var(--text-soft)" />
                  </div>
                )}
                <div style={{ fontSize: '0.7rem', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={book.title}>
                  {book.title}
                </div>
                {book.author && (
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-soft)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {book.author}
                  </div>
                )}
              </div>
            ))}
          </div>

          {enrichedBooks.length > 0 && (
            <div style={{ marginTop: '8px', fontSize: '0.7rem', color: 'var(--text-soft)', textAlign: 'right' }}>
              {GOOGLE_BOOKS_ATTRIBUTION}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MyCurations;
